<template>
    <div class="camera-selector">

        <!-- Title and Results Count -->
        <div class="camera-selector__header">
            <h1 class="camera-selector__title">{{ translations.title }}</h1>
            <div class="camera-selector__header-right">
                <div class="camera-selector__results">
                    {{ resultsCountText }}
                </div>
                <button
                    v-if="hasActiveFilters"
                    type="button"
                    @click="resetFilters"
                    class="link--button link--button--filter-reset"
                >
                    {{ translations.resetFilters || 'Reset filters' }}
                </button>
            </div>
        </div>

        <!-- Filters Section -->
        <section class="camera-selector__filters">
            <div class="camera-selector__filters-container">

                <!-- Resolution Range Slider -->
                <div class="camera-selector__filter-group" v-if="resolutionMinMax.min < resolutionMinMax.max">
                    <label class="camera-selector__label">
                        {{ resolutionLabel }} ({{ resolutionRangeDisplay.min }}-{{ resolutionRangeDisplay.max }} {{ resolutionUnit }})
                    </label>
                    <Slider
                        v-model="filters.resolutionRange"
                        :min="resolutionMinMax.min"
                        :max="resolutionMinMax.max"
                        :step="isLineScanMode ? 1 : 0.1"
                        :tooltips="true"
                        show-tooltip="focus"
                    />
                </div>

                <!-- Framerate/Line Rate Range Slider -->
                <div class="camera-selector__filter-group" v-if="framerateMinMax.min < framerateMinMax.max && !listHasNoFramerateValues">
                    <label class="camera-selector__label">
                        {{ framerateLabel }} ({{ Math.round(filters.framerateRange[0]) }}-{{ Math.round(filters.framerateRange[1]) }} {{ framerateUnit }})
                    </label>
                    <Slider
                        v-model="filters.framerateRange"
                        :min="framerateMinMax.min"
                        :max="framerateMinMax.max"
                        :step="1"
                        :tooltips="true"
                        show-tooltip="focus"
                    />
                </div>

                <!-- Pixel Size Range Slider (logarithmic track, values in µm) -->
                <div class="camera-selector__filter-group" v-if="pixelSizeMinMax.min < pixelSizeMinMax.max">
                    <label class="camera-selector__label">
                        {{ translations.pixelSize || 'Pixel Size' }} ({{ filters.pixelSizeRange[0].toFixed(2) }}-{{ filters.pixelSizeRange[1].toFixed(2) }} µm)
                    </label>
                    <Slider
                        v-model="pixelSizeSliderValue"
                        :min="Math.log(pixelSizeMinMax.min)"
                        :max="Math.log(pixelSizeMinMax.max)"
                        :step="-1"
                        :format="formatPixelSizeTooltip"
                        :tooltips="true"
                        show-tooltip="focus"
                    />
                </div>

                <!-- Chroma Filter -->
                <div class="camera-selector__filter-group" v-if="availableChromas.length > 1">
                    <label class="camera-selector__label">
                        {{ translations.chromaFilter }}
                    </label>
                    <v-select
                        v-model="filters.chroma"
                        :options="availableChromas"
                        :reduce="item => item.id"
                        label="title"
                        :placeholder="translations.choose"
                        :clearable="true"
                        :multiple="true"
                        class="camera-selector__v-select"
                    />
                </div>

                <!-- Interface Filter -->
                <div class="camera-selector__filter-group" v-if="availableInterfaces.length > 1">
                    <label class="camera-selector__label">
                        {{ translations.interfaceFilter }}
                    </label>
                    <v-select
                        v-model="filters.interface"
                        :options="availableInterfaces"
                        :reduce="item => item.id"
                        label="title"
                        :placeholder="translations.choose"
                        :clearable="true"
                        :multiple="true"
                        class="camera-selector__v-select"
                    />
                </div>

                <!-- Sensor Filter -->
                <div class="camera-selector__filter-group" v-if="availableSensors.length > 1">
                    <label class="camera-selector__label">
                        {{ translations.sensorFilter }}
                    </label>
                    <v-select
                        v-model="filters.sensor"
                        :options="availableSensors"
                        :reduce="item => item.id"
                        label="title"
                        :placeholder="translations.choose"
                        :clearable="true"
                        :multiple="true"
                        class="camera-selector__v-select"
                    />
                </div>

                <!-- Sensor Size Filter -->
                <div class="camera-selector__filter-group" v-if="availableSensorSizes.length > 1">
                    <label class="camera-selector__label">
                        {{ translations.sensorSizeFilter }}
                    </label>
                    <v-select
                        v-model="filters.sensorSize"
                        :options="availableSensorSizes"
                        :reduce="item => item.id"
                        label="title"
                        :placeholder="translations.choose"
                        :clearable="true"
                        :multiple="true"
                        class="camera-selector__v-select"
                    />
                </div>

                <!-- Spectrum Filter -->
                <div class="camera-selector__filter-group" v-if="availableProductSeries.length > 1">
                    <label class="camera-selector__label">
                        {{ translations.spectrumFilter }}
                    </label>
                    <v-select
                        v-model="filters.spectrum"
                        :options="availableSpectrums"
                        :reduce="item => item.id"
                        label="title"
                        :placeholder="translations.choose"
                        :clearable="true"
                        :multiple="true"
                        class="camera-selector__v-select"
                    />
                </div>

                <!-- Shutter Mode Filter -->
                <div class="camera-selector__filter-group" v-if="availableShutterTypes.length > 1">
                    <label class="camera-selector__label">
                        {{ translations.shutterModeFilter }}
                    </label>
                    <v-select
                        v-model="filters.shutter"
                        :options="availableShutterTypes"
                        :reduce="item => item.id"
                        label="title"
                        :placeholder="translations.choose"
                        :clearable="true"
                        :multiple="true"
                        class="camera-selector__v-select"
                    />
                </div>

                <!-- Line Scan Sub-Type Filter (only in line-scan mode) -->
                <div class="camera-selector__filter-group" v-if="availableLineScanSubTypes.length > 1">
                    <label class="camera-selector__label">
                        {{ translations.lineScanTypeFilter || 'Line Scan Type' }}
                    </label>
                    <v-select
                        v-model="filters.lineScanSubType"
                        :options="availableLineScanSubTypes"
                        :reduce="type => type.value"
                        label="label"
                        :placeholder="translations.choose"
                        :clearable="true"
                        :multiple="true"
                        class="camera-selector__v-select"
                    />
                </div>

                <!-- ProductSeries Filter -->
                <div class="camera-selector__filter-group" v-if="availableProductSeries.length > 1">
                    <label class="camera-selector__label">
                        {{ translations.productSeriesFilter }}
                    </label>
                    <v-select
                        v-model="filters.productSeries"
                        :options="availableProductSeries"
                        :reduce="series => series.id"
                        label="name"
                        :placeholder="translations.choose"
                        :clearable="true"
                        :multiple="true"
                        class="camera-selector__v-select"
                    />
                </div>

            </div>
        </section>

        <!-- Camera Results Table -->
        <section class="camera-selector__results-table">
            <div class="camera-selector__table-container">

                <!-- Camera Series Groups -->
                <div
                    v-for="series in cameraSeriesGroups"
                    :key="series.id"
                    class="camera-selector__series-group"
                    :class="{ 'camera-selector__series-group--no-header': hideFamilyGrouping }"
                >
                    <!-- Series Header with Image (only show if not hiding family grouping) -->
                    <div class="camera-selector__series-header" v-if="!hideFamilyGrouping">
                        <div class="camera-selector__series-image" v-if="series.image">
                            <img
                                :src="series.image"
                                :alt="series.name"
                            >
                        </div>
                        <div class="camera-selector__series-info">
                            <h2 class="camera-selector__series-name">{{ series.name }}</h2>
                            <a v-if="series.link" :href="series.link" class="camera-selector__series-link">
                                {{ series.linkText }}
                            </a>
                        </div>
                    </div>

                    <div class="camera-selector__table-scroll">
                    <table class="camera-selector__table">
                        <thead>
                        <tr>
                            <th class="camera-selector__th--sortable"
                                :class="{
                                    'camera-selector__th--sorted-asc': sortKey === 'model' && sortAsc,
                                    'camera-selector__th--sorted-desc': sortKey === 'model' && !sortAsc
                                }"
                                @click="setSort('model')">
                                {{ translations.model }}
                            </th>
                            <th class="camera-selector__table-column--align-right camera-selector__th--sortable"
                                :class="{
                                    'camera-selector__th--sorted-asc': sortKey === 'resolutionMP' && sortAsc,
                                    'camera-selector__th--sorted-desc': sortKey === 'resolutionMP' && !sortAsc
                                }"
                                @click="setSort('resolutionMP')">
                                MP
                            </th>
                            <th class="camera-selector__table-column--align-right camera-selector__th--sortable"
                                :class="{
                                    'camera-selector__th--sorted-asc': sortKey === 'resolution' && sortAsc,
                                    'camera-selector__th--sorted-desc': sortKey === 'resolution' && !sortAsc
                                }"
                                @click="setSort('resolution')">
                                {{ translations.resolution }}
                            </th>
                            <th class="camera-selector__th--sortable"
                                :class="{
                                    'camera-selector__th--sorted-asc': sortKey === 'sensor' && sortAsc,
                                    'camera-selector__th--sorted-desc': sortKey === 'sensor' && !sortAsc
                                }"
                                @click="setSort('sensor')">
                                {{ translations.sensor }}
                            </th>
                            <th class="camera-selector__table-column--align-right camera-selector__th--sortable"
                                :class="{
                                    'camera-selector__th--sorted-asc': sortKey === 'pixelSize' && sortAsc,
                                    'camera-selector__th--sorted-desc': sortKey === 'pixelSize' && !sortAsc
                                }"
                                @click="setSort('pixelSize')">
                                {{ translations.pixelSize }}
                            </th>
                            <th class="camera-selector__table-column--align-right camera-selector__th--sortable"
                                :class="{
                                    'camera-selector__th--sorted-asc': sortKey === 'framerate' && sortAsc,
                                    'camera-selector__th--sorted-desc': sortKey === 'framerate' && !sortAsc
                                }"
                                @click="setSort('framerate')">
                                {{ framerateColumnHeader }}
                            </th>
                            <th class="camera-selector__th--sortable"
                                :class="{
                                    'camera-selector__th--sorted-asc': sortKey === 'interface' && sortAsc,
                                    'camera-selector__th--sorted-desc': sortKey === 'interface' && !sortAsc
                                }"
                                @click="setSort('interface')">
                                {{ translations.interface }}
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="camera in series.cameras"
                                :key="camera.id"
                                class="camera-selector__row"
                                :class="{ 'camera-selector__row--clickable': camera.productSeriesPageLink }"
                                @click="navigateToCamera(camera, $event)"
                            >
                                <td>
                                    <a v-if="camera.productSeriesPageLink" :href="camera.productSeriesPageLink">
                                        {{ camera.model }}
                                    </a>
                                    <span v-else>{{ camera.model }}</span>
                                </td>
                                <td class="camera-selector__table-column--align-right" v-html="formatMp(camera.resolutionMP)"></td>
                                <td class="camera-selector__table-column--align-right" v-html="formatResolution(camera.resolutionH, camera.resolutionV, camera.sensorType)"></td>
                                <td>{{ optionLabel(camera.sensor, allSensors) }}</td>
                                <td class="camera-selector__table-column--align-right" v-html="formatPixelSize(camera.pixelSize)"></td>
                                <td class="camera-selector__table-column--align-right" v-html="formatFramerate(camera)"></td>
                                <td>{{ optionLabel(camera.interface, allInterfaces) }}</td>
                            </tr>
                        </tbody>
                    </table>
                    </div>

                </div>

            </div>
        </section>

    </div>
</template>

<script>
import Slider from '@vueform/slider';
import vSelect from 'vue-select';

export default {
    name: 'CameraSelector',

    components: {
        Slider,
        vSelect,
    },

    props: {
        apiUrl: {
            type: String,
            required: true,
        },
        defaultSensorType: {
            type: String,
            default: 'all',
        },
        hideFamilyGrouping: {
            type: Boolean,
            default: false,
        },
        translations: {
            type: Object,
            default: () => ({}),
        },
    },

    data() {
        return {
            cameras: [],
            productFamilies: [],
            productSeries: [],
            allChromas: [],
            allInterfaces: [],
            allSensors: [],
            allSensorTypes: [],
            allSensorSizes: [],
            allSpectrums: [],
            allShutterTypes: [],
            filters: {
                sensorType: this.defaultSensorType,
                lineScanSubType: [],
                resolutionRange: [0, 100],
                framerateRange: [0, 1000],
                pixelSizeRange: [0, 100],
                chroma: [],
                interface: [],
                sensor: [],
                sensorSize: [],
                spectrum: [],
                shutter: [],
                productSeries: [],
            },
            // Fixed min/max values for sliders (don't change)
            resolutionMinMax: {
                min: 0,
                max: 100,
            },
            framerateMinMax: {
                min: 0,
                max: 1000,
            },
            pixelSizeMinMax: {
                min: 0,
                max: 0,
            },
            // Flag to prevent URL updates during initial load
            isInitialLoad: true,
            sortKey: null,
            sortAsc: true,
            listHasNoFramerateValues: true,
        };
    },

    computed: {
        /**
         * Filter cameras based on active filters
         */
        filteredCameras() {
            let filtered = this.cameras;

            // Filter by sensor type
            if (this.filters.sensorType !== 'all') {
                filtered = filtered.filter(camera => {
                    // If line-scan-2d is selected, show both line-scan-2d and line-scan-3d
                    if (this.filters.sensorType === 'line-scan-2d') {
                        return camera.sensorType === 'line-scan-2d' || camera.sensorType === 'line-scan-3d';
                    }
                    return camera.sensorType === this.filters.sensorType;
                });
            }

            // Filter by line-scan sub-type (only applies in line-scan mode)
            if (this.isLineScanMode && this.filters.lineScanSubType.length > 0) {
                filtered = filtered.filter(camera => this.filters.lineScanSubType.includes(camera.sensorType));
            }

            // Filter by resolution range (allow null values)
            filtered = filtered.filter(camera => {
                // In line-scan mode, filter by ResolutionH
                if (this.isLineScanMode) {
                    if (!camera.resolutionH) return true; // Show cameras without resolution
                    return camera.resolutionH >= this.filters.resolutionRange[0] &&
                           camera.resolutionH <= this.filters.resolutionRange[1];
                }
                // In area-scan mode, filter by ResolutionMP
                if (!camera.resolutionMP) return true; // Show cameras without resolution
                return camera.resolutionMP >= this.filters.resolutionRange[0] &&
                       camera.resolutionMP <= this.filters.resolutionRange[1];
            });

            // Filter by framerate/line rate range (allow null values)
            filtered = filtered.filter(camera => {
                // In line-scan mode, filter by lineRateSearch
                if (this.isLineScanMode) {
                    if (!camera.lineRateSearch) return true; // Show cameras without line rate
                    return camera.lineRateSearch >= this.filters.framerateRange[0] &&
                           camera.lineRateSearch <= this.filters.framerateRange[1];
                }
                // In area-scan mode, filter by framerate
                if (!camera.framerate || isNaN(camera.framerate)) return true; // Show cameras without framerate
                return camera.framerate >= this.filters.framerateRange[0] &&
                       camera.framerate <= this.filters.framerateRange[1];
            });

            // Filter by pixel size range (allow null values)
            filtered = filtered.filter(camera => this.matchesPixelSizeRange(camera));

            // Filter by chroma (multi-select - camera.chroma is an array, check for any overlap)
            if (this.filters.chroma.length > 0) {
                filtered = filtered.filter(camera =>
                    camera.chroma && Array.isArray(camera.chroma) &&
                    camera.chroma.some(c => this.filters.chroma.includes(c))
                );
            }

            // Filter by interface (multi-select)
            if (this.filters.interface.length > 0) {
                filtered = filtered.filter(camera => this.filters.interface.includes(camera.interface));
            }

            // Filter by sensor (multi-select)
            if (this.filters.sensor.length > 0) {
                filtered = filtered.filter(camera => this.filters.sensor.includes(camera.sensor));
            }

            // Filter by sensor size (multi-select)
            if (this.filters.sensorSize.length > 0) {
                filtered = filtered.filter(camera => this.filters.sensorSize.includes(camera.sensorSize));
            }

            // Filter by spectrum (multi-select - camera.spectrum is an array, check for any overlap)
            if (this.filters.spectrum.length > 0) {
                filtered = filtered.filter(camera =>
                    camera.spectrum && Array.isArray(camera.spectrum) &&
                    camera.spectrum.some(s => this.filters.spectrum.includes(s))
                );
            }

            // Filter by shutter mode (multi-select - camera.shutter is an array, check for any overlap)
            if (this.filters.shutter.length > 0) {
                filtered = filtered.filter(camera =>
                    camera.shutter && Array.isArray(camera.shutter) &&
                    camera.shutter.some(s => this.filters.shutter.includes(s))
                );
            }

            // Filter by productSeries (multi-select)
            if (this.filters.productSeries.length > 0) {
                filtered = filtered.filter(camera => this.filters.productSeries.includes(camera.productSeriesID));
            }

            return filtered;
        },

        /**
         * Get available chromas based on current filtered cameras
         */
        availableChromas() {
            const ids = new Set();
            this.getIntermediateFilteredCameras('chroma').forEach(camera => {
                if (camera.chroma && Array.isArray(camera.chroma)) {
                    camera.chroma.forEach(c => ids.add(c));
                }
            });
            // No client-side sorting: the order comes from CameraOption.Sort via the API
            return this.allChromas
                .filter(item => ids.has(item.id));
        },

        /**
         * Get available interfaces based on current filtered cameras
         */
        availableInterfaces() {
            const ids = new Set();
            this.getIntermediateFilteredCameras('interface').forEach(camera => {
                if (camera.interface) {
                    ids.add(camera.interface);
                }
            });
            // No client-side sorting: the order comes from CameraOption.Sort via the API
            return this.allInterfaces
                .filter(item => ids.has(item.id));
        },

        /**
         * Get available sensors based on current filtered cameras
         */
        availableSensors() {
            const ids = new Set();
            this.getIntermediateFilteredCameras('sensor').forEach(camera => {
                if (camera.sensor) {
                    ids.add(camera.sensor);
                }
            });
            return this.allSensors
                .filter(item => ids.has(item.id))
                .sort((a, b) => a.title.localeCompare(b.title));
        },

        /**
         * Get available sensor sizes based on current filtered cameras
         */
        availableSensorSizes() {
            const ids = new Set();
            this.getIntermediateFilteredCameras('sensorSize').forEach(camera => {
                if (camera.sensorSize) {
                    ids.add(camera.sensorSize);
                }
            });
            // No client-side sorting: the order comes from CameraOption.Sort via the API
            return this.allSensorSizes
                .filter(item => ids.has(item.id));
        },

        /**
         * Get available spectrums based on current filtered cameras
         */
        availableSpectrums() {
            const ids = new Set();
            this.getIntermediateFilteredCameras('spectrum').forEach(camera => {
                if (camera.spectrum && Array.isArray(camera.spectrum)) {
                    camera.spectrum.forEach(s => ids.add(s));
                }
            });
            // No client-side sorting: the order comes from CameraOption.Sort via the API
            return this.allSpectrums
                .filter(s => ids.has(s.id));
        },

        /**
         * Get available shutter modes based on current filtered cameras
         */
        availableShutterTypes() {
            const ids = new Set();
            this.getIntermediateFilteredCameras('shutter').forEach(camera => {
                if (camera.shutter && Array.isArray(camera.shutter)) {
                    camera.shutter.forEach(s => ids.add(s));
                }
            });
            // No client-side sorting: the order comes from CameraOption.Sort via the API
            return this.allShutterTypes
                .filter(s => ids.has(s.id));
        },

        /**
         * Get available product series based on current filtered cameras
         */
        availableProductSeries() {
            const seriesIDs = new Set();
            this.getIntermediateFilteredCameras('productSeries').forEach(camera => {
                if (camera.productSeriesID) {
                    seriesIDs.add(camera.productSeriesID);
                }
            });

            // Filter productSeries to only include IDs that are in use
            return this.productSeries
                .filter(s => seriesIDs.has(s.id))
                .sort((a, b) => a.name.localeCompare(b.name));
        },

        /**
         * Get available line-scan sub-types (line-scan-2d, line-scan-3d)
         * Only shown in line-scan mode
         */
        availableLineScanSubTypes() {
            if (!this.isLineScanMode) {
                return [];
            }

            const subTypes = new Set();
            this.getIntermediateFilteredCameras('lineScanSubType').forEach(camera => {
                if (camera.sensorType === 'line-scan-2d' || camera.sensorType === 'line-scan-3d') {
                    subTypes.add(camera.sensorType);
                }
            });

            // Convert to array with display labels
            const options = [];
            if (subTypes.has('line-scan-2d')) {
                options.push({
                    value: 'line-scan-2d',
                    label: this.translations.lineScan2D || 'Line Scan 2D'
                });
            }
            if (subTypes.has('line-scan-3d')) {
                options.push({
                    value: 'line-scan-3d',
                    label: this.translations.lineScan3D || 'Line Scan 3D'
                });
            }

            return options;
        },

        /**
         * Group filtered cameras by ProductFamily or return as single group
         */
        cameraSeriesGroups() {
            // If family grouping is hidden, return all cameras in a single group
            if (this.hideFamilyGrouping) {
                const sortedCameras = this.sortCameras(this.filteredCameras);
                return [{
                    id: 0,
                    name: '',
                    cameras: sortedCameras,
                    image: null,
                    link: null,
                    linkText: null,
                }];
            }

            // Group by ProductFamily
            const groups = {};

            this.filteredCameras.forEach(camera => {
                const familyID = camera.productFamilyID || 0;
                const familyName = camera.productFamilyName || 'Other';

                if (!groups[familyID]) {
                    // Find ProductFamily data
                    const familyData = this.productFamilies.find(f => f.id === familyID);

                    groups[familyID] = {
                        id: familyID,
                        name: familyData ? familyData.title : familyName,
                        cameras: [],
                        image: familyData && familyData.imageUrl ? familyData.imageUrl : null,
                        link: familyData && familyData.link ? familyData.link : null,
                        linkText: familyData ? this.translations.moreInfo?.replace('{family}', familyData.title) : null,
                    };
                }

                groups[familyID].cameras.push(camera);
            });

            // Sort cameras within each group
            Object.values(groups).forEach(group => {
                group.cameras = this.sortCameras(group.cameras);
            });

            // Sort groups alphabetically by name
            return Object.values(groups).sort((a, b) => a.name.localeCompare(b.name));
        },

        /**
         * Get formatted results count text
         */
        resultsCountText() {
            const count = this.filteredCameras.length;
            return this.translations.resultsCount?.replace('{count}', count) || `${count} results`;
        },

        /**
         * Check if any filters are active (different from default)
         */
        hasActiveFilters() {
            return this.filters.resolutionRange[0] !== this.resolutionMinMax.min ||
                   this.filters.resolutionRange[1] !== this.resolutionMinMax.max ||
                   this.filters.framerateRange[0] !== this.framerateMinMax.min ||
                   this.filters.framerateRange[1] !== this.framerateMinMax.max ||
                   this.filters.pixelSizeRange[0] !== this.pixelSizeMinMax.min ||
                   this.filters.pixelSizeRange[1] !== this.pixelSizeMinMax.max ||
                   this.filters.chroma.length > 0 ||
                   this.filters.interface.length > 0 ||
                   this.filters.sensor.length > 0 ||
                   this.filters.sensorSize.length > 0 ||
                   this.filters.spectrum.length > 0 ||
                   this.filters.shutter.length > 0 ||
                   this.filters.lineScanSubType.length > 0 ||
                   this.filters.productSeries.length > 0;
        },

        /**
         * Check if current view is for line-scan cameras
         */
        isLineScanMode() {
            return this.defaultSensorType === 'line-scan-2d' || this.defaultSensorType === 'line-scan-3d';
        },

        /**
         * Get framerate/line rate label for slider
         */
        framerateLabel() {
            if (this.isLineScanMode) {
                return this.translations.lineRate || 'Line rate';
            }
            return this.translations.framerateRange || 'Framerate';
        },

        /**
         * Get framerate/line rate unit (fps or kHz)
         */
        framerateUnit() {
            return this.isLineScanMode ? 'kHz' : 'fps';
        },

        /**
         * Get column header for framerate/line rate
         */
        framerateColumnHeader() {
            //console.log(this.translations); // remove for production
            if (this.isLineScanMode) {
                return this.translations.lineRate || 'Line rate';
            }
            return this.translations.framerate || 'Framerate';
        },

        /**
         * Get resolution label for slider
         */
        resolutionLabel() {
            if (this.isLineScanMode) {
                return this.translations.resolutionPixel || 'Resolution (Pixel)';
            }
            return this.translations.resolution || 'Resolution';
        },

        /**
         * Get resolution unit (MP or px)
         */
        resolutionUnit() {
            return this.isLineScanMode ? 'px' : 'MP';
        },

        /**
         * Format resolution range display values
         */
        resolutionRangeDisplay() {
            if (this.isLineScanMode) {
                return {
                    min: Math.round(this.filters.resolutionRange[0]),
                    max: Math.round(this.filters.resolutionRange[1])
                };
            }
            return {
                min: this.filters.resolutionRange[0].toFixed(1),
                max: this.filters.resolutionRange[1].toFixed(1)
            };
        },

        /**
         * Pixel size slider works on a logarithmic scale: most cameras sit
         * between 2 and 10 µm, a few go up to 35 µm, so a linear track would
         * squeeze the relevant range into its first quarter.
         * The filter itself keeps plain µm values (rounded to 2 decimals).
         */
        pixelSizeSliderValue: {
            get() {
                return this.filters.pixelSizeRange.map(v => Math.log(v));
            },
            set(values) {
                const { min, max } = this.pixelSizeMinMax;
                this.filters.pixelSizeRange = values.map(v =>
                    Math.min(max, Math.max(min, Math.round(Math.exp(v) * 100) / 100))
                );
            },
        },
    },

    methods: {
        /**
         * Load camera data from API
         */
        async loadData() {
            try {
                // Check if flush parameter is in current URL
                const urlParams = new URLSearchParams(window.location.search);
                const flush = urlParams.get('flush');

                // Add flush parameter to API URL if present
                let apiUrl = this.apiUrl;
                if (flush) {
                    apiUrl += (this.apiUrl.includes('?') ? '&' : '?') + 'flush=' + flush;
                }

                const response = await fetch(apiUrl);
                const data = await response.json();

                this.cameras = data.cameras || [];
                this.productFamilies = data.productFamilies || [];
                this.productSeries = data.productSeries || [];
                this.allChromas = data.chromas || [];
                this.allInterfaces = data.interfaces || [];
                this.allSensors = data.sensors || [];
                this.allSensorTypes = data.sensorTypes || [];
                this.allSensorSizes = data.sensorSizes || [];
                this.allSpectrums = data.spectrums || [];
                this.allShutterTypes = data.shutterTypes || [];

                // Calculate min/max for sliders
                this.calculateMinMax();

                // Initialize slider values to cover full range
                this.filters.resolutionRange = [this.resolutionMinMax.min, this.resolutionMinMax.max];
                this.filters.framerateRange = [this.framerateMinMax.min, this.framerateMinMax.max];
                this.filters.pixelSizeRange = [this.pixelSizeMinMax.min, this.pixelSizeMinMax.max];

                // Load filters from URL
                this.loadFiltersFromUrl();

                // Enable URL updates after initial load
                this.$nextTick(() => {
                    this.isInitialLoad = false;
                });
            } catch (error) {
                console.error('Error loading camera data:', error);
            }
        },

        /**
         * Load filter values from URL parameters
         */
        loadFiltersFromUrl() {
            const urlParams = new URLSearchParams(window.location.search);

            if (urlParams.has('resMin')) {
                const resMin = parseFloat(urlParams.get('resMin'));
                if (!isNaN(resMin)) {
                    this.filters.resolutionRange[0] = resMin;
                }
            }

            if (urlParams.has('resMax')) {
                const resMax = parseFloat(urlParams.get('resMax'));
                if (!isNaN(resMax)) {
                    this.filters.resolutionRange[1] = resMax;
                }
            }

            if (urlParams.has('fpsMin')) {
                const fpsMin = parseFloat(urlParams.get('fpsMin'));
                if (!isNaN(fpsMin)) {
                    this.filters.framerateRange[0] = fpsMin;
                }
            }

            if (urlParams.has('fpsMax')) {
                const fpsMax = parseFloat(urlParams.get('fpsMax'));
                if (!isNaN(fpsMax)) {
                    this.filters.framerateRange[1] = fpsMax;
                }
            }

            if (urlParams.has('pxMin')) {
                const pxMin = parseFloat(urlParams.get('pxMin'));
                if (!isNaN(pxMin) && pxMin > 0) {
                    this.filters.pixelSizeRange[0] = pxMin;
                }
            }

            if (urlParams.has('pxMax')) {
                const pxMax = parseFloat(urlParams.get('pxMax'));
                if (!isNaN(pxMax) && pxMax > 0) {
                    this.filters.pixelSizeRange[1] = pxMax;
                }
            }

            if (urlParams.has('chroma')) {
                const chromas = urlParams.get('chroma').split(',').filter(v => v);
                this.filters.chroma = chromas;
            }

            if (urlParams.has('interface')) {
                const interfaces = urlParams.get('interface').split(',').filter(v => v);
                this.filters.interface = interfaces;
            }

            if (urlParams.has('sensor')) {
                const sensors = urlParams.get('sensor').split(',').filter(v => v);
                this.filters.sensor = sensors;
            }

            if (urlParams.has('sensorSize')) {
                const sensorSizes = urlParams.get('sensorSize').split(',').filter(v => v);
                this.filters.sensorSize = sensorSizes;
            }

            if (urlParams.has('spectrum')) {
                const spectrums = urlParams.get('spectrum').split(',').filter(v => v);
                this.filters.spectrum = spectrums;
            }

            if (urlParams.has('shutter')) {
                const shutterTypes = urlParams.get('shutter').split(',').filter(v => v);
                this.filters.shutter = shutterTypes;
            }

            if (urlParams.has('series')) {
                const seriesIDs = urlParams.get('series').split(',')
                    .map(id => parseInt(id))
                    .filter(id => !isNaN(id));
                this.filters.productSeries = seriesIDs;
            }

            if (urlParams.has('lineScanType')) {
                const lineScanTypes = urlParams.get('lineScanType').split(',').filter(v => v);
                this.filters.lineScanSubType = lineScanTypes;
            }
        },

        /**
         * Update URL with current filter values
         */
        updateUrl() {
            // Don't update URL during initial load
            if (this.isInitialLoad) {
                return;
            }

            const params = new URLSearchParams();

            // Only add parameters if they differ from default (full range)
            if (this.filters.resolutionRange[0] !== this.resolutionMinMax.min) {
                const resMin = this.isLineScanMode
                    ? Math.round(this.filters.resolutionRange[0]).toString()
                    : this.filters.resolutionRange[0].toFixed(1);
                params.set('resMin', resMin);
            }

            if (this.filters.resolutionRange[1] !== this.resolutionMinMax.max) {
                const resMax = this.isLineScanMode
                    ? Math.round(this.filters.resolutionRange[1]).toString()
                    : this.filters.resolutionRange[1].toFixed(1);
                params.set('resMax', resMax);
            }

            if (this.filters.framerateRange[0] !== this.framerateMinMax.min) {
                params.set('fpsMin', Math.round(this.filters.framerateRange[0]).toString());
            }

            if (this.filters.framerateRange[1] !== this.framerateMinMax.max) {
                params.set('fpsMax', Math.round(this.filters.framerateRange[1]).toString());
            }

            if (this.filters.pixelSizeRange[0] !== this.pixelSizeMinMax.min) {
                params.set('pxMin', this.filters.pixelSizeRange[0].toFixed(2));
            }

            if (this.filters.pixelSizeRange[1] !== this.pixelSizeMinMax.max) {
                params.set('pxMax', this.filters.pixelSizeRange[1].toFixed(2));
            }

            if (this.filters.chroma.length > 0) {
                params.set('chroma', this.filters.chroma.join(','));
            }

            if (this.filters.interface.length > 0) {
                params.set('interface', this.filters.interface.join(','));
            }

            if (this.filters.sensor.length > 0) {
                params.set('sensor', this.filters.sensor.join(','));
            }

            if (this.filters.sensorSize.length > 0) {
                params.set('sensorSize', this.filters.sensorSize.join(','));
            }

            if (this.filters.spectrum.length > 0) {
                params.set('spectrum', this.filters.spectrum.join(','));
            }

            if (this.filters.shutter.length > 0) {
                params.set('shutter', this.filters.shutter.join(','));
            }

            if (this.filters.productSeries.length > 0) {
                params.set('series', this.filters.productSeries.join(','));
            }

            if (this.filters.lineScanSubType.length > 0) {
                params.set('lineScanType', this.filters.lineScanSubType.join(','));
            }

            const queryString = params.toString();
            const newUrl = queryString
                ? `${window.location.pathname}?${queryString}`
                : window.location.pathname;

            // Update URL without reloading the page
            window.history.pushState({}, '', newUrl);
        },

        /**
         * Calculate fixed min/max values for sliders based on all cameras
         */
        calculateMinMax() {
            // In line-scan mode, use ResolutionH (pixel count)
            // In area-scan mode, use ResolutionMP (megapixels)
            const resolutions = this.cameras
                .map(c => this.isLineScanMode ? c.resolutionH : c.resolutionMP)
                .filter(r => r != null);

            // In line-scan mode, use lineRateSearch values
            // In area-scan mode, use framerate values
            const framerates = this.cameras
                .map(c => this.isLineScanMode ? c.lineRateSearch : c.framerate)
                .filter(f => f != null && !isNaN(f));

            if (this.isLineScanMode) {
                // For line-scan: ResolutionH is integer pixels
                this.resolutionMinMax = {
                    min: resolutions.length > 0 ? Math.floor(Math.min(...resolutions)) : 0,
                    max: resolutions.length > 0 ? Math.ceil(Math.max(...resolutions)) : 10000,
                };
            } else {
                // For area-scan: ResolutionMP is decimal megapixels
                this.resolutionMinMax = {
                    min: resolutions.length > 0 ? Math.floor(Math.min(...resolutions) * 10) / 10 : 0,
                    max: resolutions.length > 0 ? Math.ceil(Math.max(...resolutions) * 10) / 10 : 100,
                };
            }

            this.framerateMinMax = {
                min: framerates.length > 0 ? Math.floor(Math.min(...framerates)) : 0,
                max: framerates.length > 0 ? Math.ceil(Math.max(...framerates)) : 1000,
            };

            this.listHasNoFramerateValues = framerates.length === 0;

            // Pixel sizes: min/max stay 0 when no camera has a value, which hides the slider
            const pixelSizes = this.cameras
                .map(c => this.getPixelSizeValue(c))
                .filter(p => p != null && p > 0);

            this.pixelSizeMinMax = {
                min: pixelSizes.length > 0 ? Math.floor(Math.min(...pixelSizes) * 100) / 100 : 0,
                max: pixelSizes.length > 0 ? Math.ceil(Math.max(...pixelSizes) * 100) / 100 : 0,
            };
        },

        /**
         * Numeric pixel size in µm. The API delivers a formatted string
         * ("3.45" or "3.45 × 3.50" for non-square pixels); the first value
         * (horizontal) is used.
         */
        getPixelSizeValue(camera) {
            if (!camera.pixelSize) return null;
            const match = String(camera.pixelSize).match(/[\d.]+/);
            return match ? parseFloat(match[0]) : null;
        },

        /**
         * Check a camera against the pixel size range; cameras without a value always pass
         */
        matchesPixelSizeRange(camera) {
            const pixelSize = this.getPixelSizeValue(camera);
            if (pixelSize == null) return true;
            return pixelSize >= this.filters.pixelSizeRange[0] &&
                   pixelSize <= this.filters.pixelSizeRange[1];
        },

        /**
         * Tooltip formatter for the logarithmic pixel size slider
         */
        formatPixelSizeTooltip(value) {
            return `${Math.exp(value).toFixed(2)} µm`;
        },

        /**
         * Get intermediate filtered cameras (excluding one filter)
         * Used to update available options for filters
         */
        getIntermediateFilteredCameras(excludeFilter) {
            let filtered = this.cameras;

            // Filter by sensor type
            if (excludeFilter !== 'sensorType' && this.filters.sensorType !== 'all') {
                filtered = filtered.filter(camera => {
                    // If line-scan-2d is selected, show both line-scan-2d and line-scan-3d
                    if (this.filters.sensorType === 'line-scan-2d') {
                        return camera.sensorType === 'line-scan-2d' || camera.sensorType === 'line-scan-3d';
                    }
                    return camera.sensorType === this.filters.sensorType;
                });
            }

            // Filter by line-scan sub-type (only applies in line-scan mode)
            if (excludeFilter !== 'lineScanSubType' && this.isLineScanMode && this.filters.lineScanSubType.length > 0) {
                filtered = filtered.filter(camera => this.filters.lineScanSubType.includes(camera.sensorType));
            }

            // Filter by resolution range (allow null values)
            if (excludeFilter !== 'resolutionRange') {
                filtered = filtered.filter(camera => {
                    // In line-scan mode, filter by ResolutionH
                    if (this.isLineScanMode) {
                        if (!camera.resolutionH) return true; // Show cameras without resolution
                        return camera.resolutionH >= this.filters.resolutionRange[0] &&
                               camera.resolutionH <= this.filters.resolutionRange[1];
                    }
                    // In area-scan mode, filter by ResolutionMP
                    if (!camera.resolutionMP) return true; // Show cameras without resolution
                    return camera.resolutionMP >= this.filters.resolutionRange[0] &&
                           camera.resolutionMP <= this.filters.resolutionRange[1];
                });
            }

            // Filter by framerate/line rate range (allow null values)
            if (excludeFilter !== 'framerateRange') {
                filtered = filtered.filter(camera => {
                    // In line-scan mode, filter by lineRateSearch
                    if (this.isLineScanMode) {
                        if (!camera.lineRateSearch) return true; // Show cameras without line rate
                        return camera.lineRateSearch >= this.filters.framerateRange[0] &&
                               camera.lineRateSearch <= this.filters.framerateRange[1];
                    }
                    // In area-scan mode, filter by framerate
                    if (!camera.framerate || isNaN(camera.framerate)) return true; // Show cameras without framerate
                    return camera.framerate >= this.filters.framerateRange[0] &&
                           camera.framerate <= this.filters.framerateRange[1];
                });
            }

            // Filter by pixel size range (allow null values)
            if (excludeFilter !== 'pixelSizeRange') {
                filtered = filtered.filter(camera => this.matchesPixelSizeRange(camera));
            }

            // Filter by chroma (multi-select - camera.chroma is an array, check for any overlap)
            if (excludeFilter !== 'chroma' && this.filters.chroma.length > 0) {
                filtered = filtered.filter(camera =>
                    camera.chroma && Array.isArray(camera.chroma) &&
                    camera.chroma.some(c => this.filters.chroma.includes(c))
                );
            }

            // Filter by interface (multi-select)
            if (excludeFilter !== 'interface' && this.filters.interface.length > 0) {
                filtered = filtered.filter(camera => this.filters.interface.includes(camera.interface));
            }

            // Filter by sensor (multi-select)
            if (excludeFilter !== 'sensor' && this.filters.sensor.length > 0) {
                filtered = filtered.filter(camera => this.filters.sensor.includes(camera.sensor));
            }

            // Filter by sensor size (multi-select)
            if (excludeFilter !== 'sensorSize' && this.filters.sensorSize.length > 0) {
                filtered = filtered.filter(camera => this.filters.sensorSize.includes(camera.sensorSize));
            }

            // Filter by spectrum (multi-select)
            if (excludeFilter !== 'spectrum' && this.filters.spectrum.length > 0) {
                filtered = filtered.filter(camera =>
                    camera.spectrum && Array.isArray(camera.spectrum) &&
                    camera.spectrum.some(s => this.filters.spectrum.includes(s))
                );
            }

            // Filter by shutter mode (multi-select)
            if (excludeFilter !== 'shutter' && this.filters.shutter.length > 0) {
                filtered = filtered.filter(camera =>
                    camera.shutter && Array.isArray(camera.shutter) &&
                    camera.shutter.some(s => this.filters.shutter.includes(s))
                );
            }

            // Filter by productSeries (multi-select)
            if (excludeFilter !== 'productSeries' && this.filters.productSeries.length > 0) {
                filtered = filtered.filter(camera => this.filters.productSeries.includes(camera.productSeriesID));
            }

            return filtered;
        },


        /**
         * Resolve an option ID to its display title using a pre-loaded {id, title} array.
         * Falls back to the raw value if no match is found.
         */
        optionLabel(value, options) {
            if (!value) return '-';
            const match = options.find(item => item.id === value);
            return match ? match.title : value;
        },

        /**
         * Format resolution for display
         * Area Scan: "12.4 MP - 4128 (H) × 3008 (V)"
         * Line Scan: "4096 × 4 lines"
         */
        formatResolution(h, v, sensorType) {
            // For line-scan cameras: "H × V lines"
            if (this.isLineScanMode) {
                if (!h || !v) return '-';
                return `${h.toLocaleString()}&nbsp;×&nbsp;${v.toLocaleString()}&nbsp;<span class="camera-selector__table-column-unit">lines</span>`;
            }

            // For area-scan cameras
            if (!h || !v) return '-';

            let parts = [];
            if (h && v) {
                parts.push(`${h}&nbsp;<span class="camera-selector__table-column-unit">(H)&nbsp;×&nbsp;</span>${v}&nbsp;<span class="camera-selector__table-column-unit">(V)</span>`);
            }

            return parts.join(' - ');
        },

        /**
         * Format resolution for display
         * Area Scan: "12.4 MP"
         * Line Scan: "-"
         */
        formatMp(mp) {
            if (mp) {
                return `${mp.toFixed(1)}&nbsp;<span class="camera-selector__table-column-unit">MP</span>`;
            }


            return '-';
        },

        /**
         * Format framerate for display
         * Line Scan Mode: uses lineRateSearch in kHz
         * Area Scan Mode: uses framerate in fps
         */
        formatFramerate(camera) {
            // In line-scan mode, use lineRateSearch
            if (this.isLineScanMode) {
                if (!camera.lineRateSearch) return '-';
                return `${camera.lineRateSearch.toFixed(1)} <span class="camera-selector__table-column-unit">kHz</span>`;
            }

            // In area-scan mode (or default), use framerate
            if (!camera.framerate || isNaN(camera.framerate)) return '-';
            return `${camera.framerate.toFixed(1)} <span class="camera-selector__table-column-unit">fps</span>`;
        },

        /**
         * Format pixel size for display (e.g. "3.45 µm") or return "-" if not available
         * @param pixelSize
         * @returns {string}
         */
        setSort(key) {
            if (this.sortKey === key) {
                this.sortAsc = !this.sortAsc;
            } else {
                this.sortKey = key;
                this.sortAsc = true;
            }
        },

        getSortValue(camera, key) {
            switch (key) {
                case 'model':      return camera.model || null;
                case 'resolutionMP': return camera.resolutionMP != null ? parseFloat(camera.resolutionMP) : null;
                case 'resolution': return (camera.resolutionH && camera.resolutionV) ? camera.resolutionH * camera.resolutionV : null;
                case 'sensor':     return camera.sensor || null;
                case 'pixelSize':  return this.getPixelSizeValue(camera);
                case 'framerate':  return camera.framerate != null ? parseFloat(camera.framerate)
                                        : (camera.lineRateSearch != null ? parseFloat(camera.lineRateSearch) : null);
                case 'interface':  return camera.interface || null;
                default:           return null;
            }
        },

        sortCameras(cameras) {
            const key = this.sortKey || 'model';
            const asc = this.sortKey ? this.sortAsc : true;
            return [...cameras].sort((a, b) => {
                const aVal = this.getSortValue(a, key);
                const bVal = this.getSortValue(b, key);
                if (aVal == null && bVal == null) return 0;
                if (aVal == null) return 1;
                if (bVal == null) return -1;
                if (typeof aVal === 'number') return asc ? aVal - bVal : bVal - aVal;
                const cmp = String(aVal).localeCompare(String(bVal));
                return asc ? cmp : -cmp;
            });
        },

        navigateToCamera(camera, event) {
            if (!camera.productSeriesPageLink) {
                return;
            }

            // Modifier clicks are the browser's job — it already opens a new tab or
            // window, so navigating here as well would hijack the current one.
            if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
                return;
            }

            // The model cell holds a real link that navigates on its own.
            if (event.target.closest('a')) {
                return;
            }

            window.location.href = camera.productSeriesPageLink;
        },

        formatPixelSize(pixelSize) {
            if (pixelSize == null) {
                return '-';
            }
            return `${pixelSize} <span class="camera-selector__table-column-unit">µm</span>`;
        },

        /**
         * Reset all filters to default values
         */
        resetFilters() {
            this.filters.resolutionRange = [this.resolutionMinMax.min, this.resolutionMinMax.max];
            this.filters.framerateRange = [this.framerateMinMax.min, this.framerateMinMax.max];
            this.filters.pixelSizeRange = [this.pixelSizeMinMax.min, this.pixelSizeMinMax.max];
            this.filters.chroma = [];
            this.filters.interface = [];
            this.filters.sensor = [];
            this.filters.sensorSize = [];
            this.filters.spectrum = [];
            this.filters.shutter = [];
            this.filters.lineScanSubType = [];
            this.filters.productSeries = [];
        },
    },

    watch: {
        /**
         * Auto-update URL when resolution range changes
         */
        'filters.resolutionRange': {
            handler() {
                this.updateUrl();
            },
            deep: true,
        },

        /**
         * Auto-update URL when framerate range changes
         */
        'filters.framerateRange': {
            handler() {
                this.updateUrl();
            },
            deep: true,
        },

        /**
         * Auto-update URL when pixel size range changes
         */
        'filters.pixelSizeRange': {
            handler() {
                this.updateUrl();
            },
            deep: true,
        },

        /**
         * Auto-update URL when interface filter changes
         */
        'filters.chroma'() {
            this.updateUrl();
        },

        'filters.interface'() {
            this.updateUrl();
        },

        /**
         * Auto-update URL when sensor filter changes
         */
        'filters.sensor'() {
            this.updateUrl();
        },

        /**
         * Auto-update URL when sensor size filter changes
         */
        'filters.sensorSize'() {
            this.updateUrl();
        },

        /**
         * Auto-update URL when spectrum filter changes
         */
        'filters.spectrum'() {
            this.updateUrl();
        },

        /**
         * Auto-update URL when shutter filter changes
         */
        'filters.shutter'() {
            this.updateUrl();
        },

        /**
         * Auto-update URL when productSeries filter changes
         */
        'filters.productSeries'() {
            this.updateUrl();
        },

        /**
         * Auto-update URL when lineScanSubType filter changes
         */
        'filters.lineScanSubType'() {
            this.updateUrl();
        },
    },

    mounted() {
        this.loadData();
    },
};
</script>
